import { FC, useCallback } from 'react';
import S from './EventCard.module.scss';
import { AttendeeTicket } from 'src/types/types';
import { Gallery } from 'src/redesign/components/Gallery';
import { Text } from 'src/redesign/components/Text';
import calendarIcon from 'src/redesign/assets/calendar-icon.svg';
import locationIcon from 'src/redesign/assets/location-icon.svg';
import { ArrowRight } from 'wix-ui-icons-common';
import { attendeeEventsSidebarActions } from 'src/redesign/features/AttendeeEventsSidebar/store/reducer';
import { useAppDispatch } from 'src/hooks';
import { RedesignButton } from '@components/RedesignButton';
import { useHistory, useLocation } from 'react-router-dom';

type EventCardProps = {
  key?: string;
  ticketInfo: AttendeeTicket;
};

const DEFAULT_IMAGE = 'http://cdn.ticketspot.io/no-image-default.png';

const getStatusInfo = (status: string) => {
  switch (status) {
    case 'attending':
      return { text: 'Attending', className: S.statusAttending };
    case 'notAttending':
      return { text: 'Not Going', className: S.statusNotAttending };
    case 'pending':
      return { text: 'Pending Approval', className: S.statusPending };
    case 'waitlist':
      return { text: 'On Waitlist', className: S.statusWaitlist };
    case 'cancelled':
      return { text: 'Cancelled', className: S.statusCancelled };
    default:
      return { text: status, className: S.statusDefault };
  }
};

export const EventCard: FC<EventCardProps> = ({ ticketInfo, key }) => {
  const { event } = ticketInfo;
  const actions = attendeeEventsSidebarActions;
  const dispatch = useAppDispatch();
  const statusInfo = getStatusInfo(ticketInfo.status);
  const history = useHistory();
  const location = useLocation();

  // Prepare image for gallery
  const images = [event.image_url || DEFAULT_IMAGE];
  if (event.secondary_image_urls?.length > 0) {
    images.push(...event.secondary_image_urls);
  }

  // Use date_time_display from API if available, otherwise fallback to formatted_date
  const dateTimeDisplay = event.date_time_display || event.formatted_date || '';
  const isRecurring = !!event.series_id;

  const handleViewTicketInCart = useCallback(() => {
    // Check if mobile (viewport width < 768px) at the time of click
    const isMobile = window.innerWidth < 768;

    // Set the current event in Redux state regardless of device
    dispatch(actions.setCurrentEvent(ticketInfo));

    if (isMobile) {
      // On mobile, route to dedicated page
      history.push(`/attendee-dashboard/ticket/${ticketInfo.cart_id}/order`);
    } else {
      // On desktop, open sidebar
      dispatch(actions.showAttendeeEventsSidebar());
      history.push({
        pathname: `${location.pathname}/ticket/${ticketInfo.cart_id}`,
      });
    }
  }, [dispatch, ticketInfo, history, location, actions]);

  return (
    <div className={S.eventCard} key={key}>
      <div className={`${S.status} ${statusInfo.className}`}>{statusInfo.text}</div>

      <Gallery images={images} />
      <div className={S.ticketInfoContainer}>
        <Text className={S.ticketTitle} variation="body-large" color="black-400">
          {event.title}
        </Text>
        <div className={S.informationBlock}>
          <div className={S.dateInfo}>
            <img src={calendarIcon} alt="" style={{ fontSize: '25px' }} />
            <div className={S.dateContainer}>
              <Text>{dateTimeDisplay}</Text>
              {isRecurring && (
                <span className={S.recurringBadge}>
                  <i className="pi pi-sync" style={{ fontSize: '25px' }} />
                  <Text variation="caption">Multiple dates available</Text>
                </span>
              )}
            </div>
          </div>
          {event.location && (
            <div className={S.positionInfo}>
              <img src={locationIcon} alt="" /> {event.location}
            </div>
          )}
        </div>
        <RedesignButton onClick={handleViewTicketInCart} className={S.detailsButton} height={43}>
          <Text regular color="black-400">
            View Tickets
          </Text>
          <ArrowRight style={{ stroke: 'black' }} />
        </RedesignButton>
      </div>
    </div>
  );
};
